import type { AiCostSummaryFetchedPayload } from '@Application/AiCosts/Actions/aiCostSummaryFetched';
import PlainDate from '@Domain/PlainDate';
import AiCostAudioView from '@UI/Store/AiCosts/Views/AiCostAudioView';
import AiCostDocumentsView from '@UI/Store/AiCosts/Views/AiCostDocumentsView';
import AiCostQuantitiesView from '@UI/Store/AiCosts/Views/AiCostQuantitiesView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `from` and `to` are the effective bounds the server applied; `accountId` and `queryKey` (period and
 * axis filters) scope the stored value. `callCount`, `unpricedCallCount` and `quantities` are `null`
 * outside the super-administrator's view, which is not the same as zero.
 */
class AiCostSummaryView {
	readonly accountId: number;
	readonly queryKey: string;
	readonly from: PlainDate | null;
	readonly to: PlainDate | null;
	readonly previousFrom: PlainDate | null;
	readonly previousTo: PlainDate | null;
	readonly previousCostAmount: number;
	readonly hasActivity: boolean;
	readonly callCount: number | null;
	readonly costAmount: number;
	readonly unpricedCallCount: number | null;
	readonly audio: AiCostAudioView;
	readonly documents: AiCostDocumentsView;
	readonly quantities: AiCostQuantitiesView | null;

	constructor(payload: AiCostSummaryFetchedPayload, accountId: number, queryKey: string) {
		this.accountId = accountId;
		this.queryKey = queryKey;
		this.from = PlainDate.fromApi(payload.period.from);
		this.to = PlainDate.fromApi(payload.period.to);
		this.previousFrom = PlainDate.fromApi(payload.previousPeriod.from);
		this.previousTo = PlainDate.fromApi(payload.previousPeriod.to);
		this.previousCostAmount = payload.previousPeriod.cost;
		this.hasActivity = payload.hasActivity;
		this.callCount = payload.callCount;
		this.costAmount = payload.cost.amount;
		this.unpricedCallCount = payload.cost.unpricedCallCount;
		this.audio = new AiCostAudioView(payload.audio);
		this.documents = new AiCostDocumentsView(payload.documents);
		this.quantities = payload.quantities === null ? null : new AiCostQuantitiesView(payload.quantities);

		Object.freeze(this);
	}

	get hasQuantities(): boolean {
		return this.quantities !== null;
	}

	get unpricedItemCount(): number {
		return this.audio.unpricedCount + this.documents.unpricedCount;
	}

	/** Change against the previous period, in percent; `null` when the previous period cost nothing. */
	get evolutionPercent(): number | null {
		if (this.previousCostAmount === 0) {
			return null;
		}

		return ((this.costAmount - this.previousCostAmount) / this.previousCostAmount) * 100;
	}
}

export default AiCostSummaryView;
